"use client";

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { Badge } from "@/app/components/ui/Badge";
import { Button } from "@/app/components/ui/Button";
import { Checkbox } from "@/app/components/ui/Checkbox";
import { Input } from "@/app/components/ui/Input";
import { Select } from "@/app/components/ui/Select";
import {
  IndustrySetupQuestionStage,
  IndustrySetupWizardShell,
} from "@/app/components/ui/IndustrySetupWizard";
import { useWorkspace } from "@/app/components/workspace/WorkspaceContext";
import {
  useCompleteIndustrySetup,
  useCreateIndustryWorkspace,
  useIndustrySetupContext,
  useSelectIndustryWorkspace,
} from "../hooks/useIndustrySetup";
import { useWorkspaceApplicationCatalogQuery } from "../hooks/useWorkspaceApplicationCatalogQuery";
import { useWorkspacePresetsQuery } from "../hooks/useWorkspacePresetsQuery";
import {
  canResumeIndustrySetupDraft,
  readIndustrySetupDraft,
  removeIndustrySetupDraft,
  writeIndustrySetupDraft,
  type IndustrySetupDraft,
} from "../onboarding/draft";
import type { IndustrySetupDriver, IndustrySetupInput } from "../onboarding/types";
import { resetCourierWorkspaceSelections } from "../onboarding/courierSetup";
import { presetApplyErrorMessage } from "../presetApplyFeedback";

const NEW_WORKSPACE = "__new__";

export function IndustryOnboardingWizard({
  driver,
  onClose,
}: {
  driver: IndustrySetupDriver;
  onClose: () => void;
}) {
  const t = useTranslations("apps.industrySetup");
  const messages = useTranslations();
  const router = useRouter();
  const { user, workspaces, activeWorkspace, reload } = useWorkspace();
  const draftKey = `industry-setup-draft:${user.uuid}:${driver.presetCode}`;
  const [draft, setDraft] = useState<IndustrySetupDraft>(() => {
    const saved = readIndustrySetupDraft(draftKey);
    if (saved && canResumeIndustrySetupDraft(saved, workspaces)) {
      const initial = driver.createInput?.(saved.input.displayName, t);
      return initial ? {
        ...saved,
        input: {
          ...initial,
          ...saved.input,
          salonSetup: saved.input.salonSetup ?? initial.salonSetup,
          courierSetup: saved.input.courierSetup ?? initial.courierSetup,
          personalTrainerSetup: saved.input.personalTrainerSetup ?? (initial.personalTrainerSetup ? {
            services: initial.personalTrainerSetup.services.map((service) => ({
              ...service,
              durationMinutes: saved.input.defaultDurationMinutes ?? service.durationMinutes,
            })),
          } : undefined),
        },
      } : saved;
    }
    const workspace = activeWorkspace ?? (workspaces.length === 1 ? workspaces[0] : null);
    const name = user.name?.trim();
    const displayName = driver.usePersonalName !== false && name && name !== user.email && !name.includes("@")
      ? name
      : t(driver.defaultNameKey);
    return {
      workspaceUuid: workspace?.uuid ?? "",
      workspaceName: workspace?.name ?? displayName,
      stage: "workspace",
      input: driver.createInput?.(displayName, t, { name: name && name !== user.email ? name : "", email: user.email }) ?? { ...driver.defaultInput, displayName },
    };
  });
  const draftRef = useRef(draft);
  const [workspaceSelection, setWorkspaceSelection] = useState(
    draft.workspaceUuid || (workspaces.length === 0 ? NEW_WORKSPACE : ""),
  );
  const [busy, setBusy] = useState(false);
  const runningRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const workspaceUuid = draft.stage === "setup" ? draft.workspaceUuid : "";
  const contextQuery = useIndustrySetupContext(workspaceUuid, driver.presetCode);
  const presetsQuery = useWorkspacePresetsQuery(workspaceUuid);
  const applicationCatalogQuery = useWorkspaceApplicationCatalogQuery(workspaceUuid);
  const createWorkspaceMutation = useCreateIndustryWorkspace();
  const completeMutation = useCompleteIndustrySetup(workspaceUuid, driver);
  const selectWorkspaceMutation = useSelectIndustryWorkspace();
  const context = contextQuery.data;
  const preset = presetsQuery.data?.presets.find((item) => item.code === driver.presetCode);
  const appTitles = new Map((applicationCatalogQuery.data?.applications ?? []).map((application) => [
    application.appCode,
    messages(`${application.translationKey}.title`),
  ]));
  const selectedApps = [...new Set([
    ...draft.input.selectedRecommendedAppCodes ?? context?.selectedRecommendedAppCodes ?? preset?.recommendedAppCodes ?? [],
    ...draft.input.installOnly ? [] : driver.requiredRecommendedApps,
  ])].filter((code) => preset?.recommendedAppCodes.includes(code));
  const input: IndustrySetupInput = { ...draft.input, selectedRecommendedAppCodes: selectedApps };
  const resolved = context ? driver.resolveSelections(context, input) : undefined;
  const loading = draft.stage === "setup" && (contextQuery.isLoading || presetsQuery.isLoading);
  const loadError = draft.stage === "setup" && (contextQuery.isError || presetsQuery.isError);
  const supported = context?.supported && preset?.readiness === "production";
  const nameValid = input.displayName.trim().length > 0 && input.displayName.trim().length <= (driver.displayNameMaxLength ?? 255);
  const canComplete = !busy && !loading && !loadError && supported && nameValid && driver.isValid(input) && !resolved?.needsSelection;
  const industryTitle = messages(driver.titleKey);
  const Fields = driver.Fields;
  const setupStepCount = input.installOnly ? 1 : driver.setupSteps?.length ?? 1;
  const setupStep = Math.min(draft.setupStep ?? 0, setupStepCount - 1);
  const lastSetupStep = setupStep === setupStepCount - 1;
  const canAdvance = !busy && !loading && !loadError && supported && nameValid
    && !resolved?.needsSelection && (driver.isStepValid?.(input, setupStep) ?? driver.isValid(input));

  function updateDraft(patch: Partial<IndustrySetupDraft>) {
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    setDraft(next);
    writeIndustrySetupDraft(draftKey, next);
  }

  function updateInput(patch: Partial<IndustrySetupInput>) {
    updateDraft({ input: { ...draftRef.current.input, ...patch } });
    setError(null);
  }

  async function next() {
    if (runningRef.current || !nameValid || !workspaceSelection) return;
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      if (workspaceSelection === NEW_WORKSPACE) {
        const workspace = await createWorkspaceMutation.mutateAsync(input.displayName.trim());
        // Persist the returned identity before refreshing the session or advancing the UI.
        updateDraft({ workspaceUuid: workspace.uuid, workspaceName: workspace.name, stage: "setup" });
        setWorkspaceSelection(workspace.uuid);
      } else {
        const workspace = workspaces.find((item) => item.uuid === workspaceSelection);
        if (!workspace && workspaceSelection !== draft.workspaceUuid) return;
        updateDraft({
          workspaceUuid: workspaceSelection,
          workspaceName: workspace?.name ?? draft.workspaceName,
          stage: "setup",
        });
      }
      setDirection("forward");
    } catch (failure) {
      setError(presetApplyErrorMessage(failure, t("workspace.error")));
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  async function complete() {
    if (!canComplete || runningRef.current || !resolved) return;
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await completeMutation.mutateAsync({
        ...input,
        ...resolved.selections,
        displayName: input.displayName.trim(),
      });
      await selectWorkspaceMutation.mutateAsync(workspaceUuid);
      await reload(workspaceUuid);
      removeIndustrySetupDraft(draftKey);
      onClose();
      router.replace(driver.destination(workspaceUuid, result));
    } catch (failure) {
      setError(presetApplyErrorMessage(failure, t("setup.error")));
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  function close() {
    if (!busy) {
      writeIndustrySetupDraft(draftKey, draftRef.current);
      onClose();
    }
  }

  return (
    <IndustrySetupWizardShell
      direction={direction}
      headerStart={<span className="text-sm font-semibold">{industryTitle}</span>}
      headerEnd={<Button disabled={busy} size="sm" type="button" variant="ghost" onClick={close}>{t("later")}</Button>}
      loading={busy ? {
        title: t(draft.stage === "workspace" ? "workspace.preparing" : "setup.preparing"),
        description: driver.showGuidance === false ? undefined : t("setup.preparingDescription"),
        progressLabel: t("setup.preparing"),
      } : null}
      progressLabel={t("progress", { step: draft.stage === "workspace" ? 1 : setupStep + 2, total: setupStepCount + 1 })}
      stageKey={`${draft.stage}:${workspaceUuid}:${setupStep}`}
      stepCount={setupStepCount + 1}
      stepIndex={draft.stage === "workspace" ? 0 : setupStep + 1}
      feedback={error ? <div className="flash-error" role="alert">{error}</div> : null}
      backAction={draft.stage === "setup" ? (
        <Button disabled={busy} type="button" variant="ghost" onClick={() => {
          setDirection("backward");
          updateDraft(setupStep > 0 ? { setupStep: setupStep - 1 } : { stage: "workspace" });
          setError(null);
        }}>{t("back")}</Button>
      ) : null}
      primaryAction={draft.stage === "workspace" ? (
        <Button disabled={!nameValid || !workspaceSelection || busy} form="industry-workspace-form" type="submit">
          {t("continue")}
        </Button>
      ) : (
        <Button disabled={!(lastSetupStep ? canComplete : canAdvance)} form="industry-setup-form" type="submit">
          {t(!lastSetupStep ? "continue" : input.installOnly ? "setup.installOnlyAction" : driver.completionLabelKey)}
        </Button>
      )}
    >
      {draft.stage === "workspace" ? (
        <IndustrySetupQuestionStage title={t(driver.workspaceTitleKey ?? "workspace.title")} description={t(driver.descriptionKey)}>
          <form className="max-w-xl space-y-5" id="industry-workspace-form" onSubmit={(event) => {
            event.preventDefault();
            void next();
          }}>
            <Input
              autoComplete="name"
              label={t(driver.displayNameLabelKey ?? "workspace.displayName")}
              maxLength={driver.displayNameMaxLength ?? 255}
              required
              value={input.displayName}
              onChange={(event) => updateInput({ displayName: event.target.value })}
            />
            {workspaces.length > 0 ? (
              <Select label={t("workspace.choose")} required value={workspaceSelection} onChange={(event) => {
                const uuid = event.target.value;
                setWorkspaceSelection(uuid);
                if (uuid !== draft.workspaceUuid) {
                  updateDraft({
                    workspaceUuid: uuid === NEW_WORKSPACE ? "" : uuid,
                    workspaceName: workspaces.find((item) => item.uuid === uuid)?.name ?? input.displayName,
                    input: {
                      displayName: input.displayName,
                      defaultDurationMinutes: input.defaultDurationMinutes,
                      salonSetup: input.salonSetup,
                      personalTrainerSetup: input.personalTrainerSetup,
                      courierSetup: resetCourierWorkspaceSelections(input.courierSetup),
                      installOnly: input.installOnly,
                      selectedRecommendedAppCodes: null,
                    },
                  });
                }
              }}>
                <option value="">{t("workspace.choosePlaceholder")}</option>
                {workspaces.map((workspace) => <option key={workspace.uuid} value={workspace.uuid}>{workspace.name}</option>)}
                {draft.workspaceUuid && !workspaces.some((item) => item.uuid === draft.workspaceUuid) ? (
                  <option value={draft.workspaceUuid}>{draft.workspaceName}</option>
                ) : null}
                <option value={NEW_WORKSPACE}>{t("workspace.createNew")}</option>
              </Select>
            ) : null}
            {driver.showGuidance !== false ? <p className="text-sm leading-6 text-text-secondary">
              {t(workspaceSelection === NEW_WORKSPACE ? "workspace.newDescription" : "workspace.existingDescription")}
            </p> : null}
          </form>
        </IndustrySetupQuestionStage>
      ) : (
        <IndustrySetupQuestionStage title={t(!input.installOnly && driver.setupSteps ? driver.setupSteps[setupStep] : "setup.title")} description={driver.showGuidance === false ? undefined : t("setup.description", { workspace: draft.workspaceName })}>
          {loading ? (
            <p className="text-sm text-text-secondary" role="status">{t("setup.loading")}</p>
          ) : loadError ? (
            <div className="flash-error" role="alert">
              <p>{t("setup.loadError")}</p>
              <Button className="mt-3" type="button" variant="secondary" onClick={() => {
                void contextQuery.refetch();
                void presetsQuery.refetch();
              }}>{t("retry")}</Button>
            </div>
          ) : context && preset && supported ? (
            <form className="space-y-6" id="industry-setup-form" onSubmit={(event) => {
              event.preventDefault();
              if (lastSetupStep) {
                void complete();
              } else if (canAdvance) {
                setDirection("forward");
                updateDraft({ setupStep: setupStep + 1 });
              }
            }}>
              <Fields step={setupStep} context={context} input={input} onChange={updateInput} />
              {lastSetupStep ? <details className="rounded-xl border border-border p-4">
                <summary className="cursor-pointer text-sm font-semibold text-text">{t("applications.customize")}</summary>
                <p className="mt-3 text-sm leading-6 text-text-secondary">{t(driver.allowInstallOnly === false ? "applications.guidedDescription" : "applications.description")}</p>
                {driver.allowInstallOnly !== false ? <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm">
                  <Checkbox checked={input.installOnly ?? false} onChange={(event) => updateInput({ installOnly: event.target.checked })} />
                  <span>{t("applications.installOnly")}</span>
                </label> : null}
                <div className="mt-5 flex flex-wrap gap-2">
                  {preset.requiredAppCodes.map((code) => <Badge key={code}>{appTitles.get(code) ?? code}</Badge>)}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {preset.recommendedAppCodes.map((code) => (
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg bg-bg-elevated p-3 text-sm" key={code}>
                      <Checkbox
                        checked={selectedApps.includes(code)}
                        disabled={!input.installOnly && driver.requiredRecommendedApps.includes(code)}
                        onChange={(event) => updateInput({
                          selectedRecommendedAppCodes: event.target.checked
                            ? [...selectedApps, code]
                            : selectedApps.filter((item) => item !== code),
                        })}
                      />
                      <span>{appTitles.get(code) ?? code}</span>
                    </label>
                  ))}
                </div>
              </details> : null}
              {driver.showGuidance !== false ? <p className="text-xs leading-5 text-text-muted">{t("setup.editLater")}</p> : null}
            </form>
          ) : (
            <p className="flash-error" role="alert">{t("setup.unavailable")}</p>
          )}
        </IndustrySetupQuestionStage>
      )}
    </IndustrySetupWizardShell>
  );
}
